<template>
    <ds-list-item class="message-history-item">
        <template #description>
            <div class="item-description">
                <div class="description-header">
                    <span class="contact-name"> {{ contactName }} </span>

                    <ds-popover
                        v-if="phoneType"
                        class="phone-type-popover"
                        position="top"
                        position-fixed
                    >
                        <template #reference>
                            <button class="phone-type">
                                <ds-status emphasis="low">
                                    {{ phoneType }}
                                </ds-status>
                            </button>
                        </template>

                        <template #default>
                            <div class="phone-number">
                                {{ phoneNumber }}
                            </div>
                        </template>
                    </ds-popover>

                    <ds-popover
                        v-if="!loading"
                        class="opt-status-popover"
                        position="top"
                        position-fixed
                    >
                        <template #reference>
                            <button class="opt-status">
                                <ds-status :type="optStatusType">
                                    {{ optStatusLabel }}
                                </ds-status>
                            </button>
                        </template>

                        <template #default>
                            <div class="opt-status-tooltip">
                                <span v-if="isOptedIn">
                                    {{ contactName }} replied <strong>YES</strong> from this phone number after receiving a text message from your Keap Marketing number. They're <strong>opted in</strong> to receive future texts.
                                </span>

                                <span v-else-if="isOptedOut">
                                    {{ contactName }} replied <strong>NO</strong> from this phone number after receiving a text message from your Keap Marketing number. They've <strong>opted out</strong> of receiving future texts.
                                </span>

                                <span v-else>
                                    We sent your text message from your Keap Marketing Number, and are <strong>waiting for your recipient to opt in</strong> by replying YES.
                                </span>
                            </div>
                        </template>
                    </ds-popover>
                </div>

                <div class="description-info">
                    <span class="via"> sent via: </span>

                    <tooltip-with-delay position="right">
                        <ds-chip slot="reference">
                            <template #leading>
                                <ds-icon :name="sourceIcon" />
                            </template>

                            <template #default>
                                {{ sourceName }}
                            </template>
                        </ds-chip>

                        {{ sourceToolTipText }}
                    </tooltip-with-delay>
                </div>

                <div class="message">
                    {{ message.content }}
                </div>
            </div>

            <ds-inline-alert
                v-if="!sourceTitle || sourceTitle === ''"
                type="critical"
                class="deleted-item-alert"
            >
                This {{ alertTypeText }} no longer exists.
            </ds-inline-alert>
        </template>

        <template #trailing>
            <span>Sent: {{ date }}</span>
        </template>
    </ds-list-item>
</template>

<script>
import moment from 'moment';
import { normalizePhoneNumber } from '@/components/broadcast/utils/sms-utils';
import { formatPhone } from '@/utils/phone.util';
import { SMS_OPT_STATUSES } from '@/components/broadcast/constants/smsBroadcast.constants';
import TooltipWithDelay from '../shared/TooltipWithDelay';

const { SINGLE_OPT_IN, DOUBLE_OPT_IN, OPT_OUT } = SMS_OPT_STATUSES;

const PHONE_TYPES = ['MOBILE', 'WORK', 'HOME', 'OTHER'];
const BROADCAST = 'BROADCAST';
const EASY_AUTOMATION = 'EASY_AUTOMATION';
const ADVANCED_AUTOMATION = 'ADVANCED_AUTOMATION';
const TEXT_MESSAGE_BROADCAST = 'Text message broadcast';
const CAMPAIGN_AUTOMATION = 'Campaign automation';

export default {
    components: {
        TooltipWithDelay,
    },

    props: {
        message: {
            type: Object,
            default: () => ({}),
        },

        contact: {
            type: Object,
            default: () => ({}),
        },

        optStatus: String,

        loading: Boolean,

        sourceTitle: String,
    },

    computed: {
        contactName() {
            return `${this.contact?.firstName} ${this.contact?.lastName}`;
        },

        date() {
            return this.getConversationDate({ updateTime: this.message.updated });
        },

        phones() {
            if (!this.contact) return [];

            const {
                phone1,
                phone2,
                phone3,
                phone4,
                phone5,
            } = this.contact;

            return [
                phone1,
                phone2,
                phone3,
                phone4,
                phone5,
            ].filter((item) => item);
        },

        phoneNumber() {
            return formatPhone(this.message?.toPhoneNumber);
        },

        phoneType() {
            const { toPhoneNumber } = this.message;
            const { type } = this.phones.find(({ number }) => normalizePhoneNumber(number) === normalizePhoneNumber(toPhoneNumber)) || {};

            if (type && PHONE_TYPES.includes(type.toUpperCase())) {
                return `${type.charAt(0).toUpperCase()}${type.slice(1).toLowerCase()}`;
            }

            return '';
        },

        alertTypeText() {
            if (this.message.messageType === BROADCAST) {
                return 'text message broadcast';
            }

            return 'campaign automation';
        },

        sourceIcon() {
            switch (this.message.messageType) {
            case BROADCAST:
                return 'megaphone';
            case EASY_AUTOMATION:
            case ADVANCED_AUTOMATION:
                return 'messages-automated';
            default:
                return '';
            }
        },

        sourceToolTipText() {
            return this.message.messageType === BROADCAST ? TEXT_MESSAGE_BROADCAST : CAMPAIGN_AUTOMATION;
        },

        sourceName() {
            if (!this.sourceTitle || this.sourceTitle === '') {
                return this.message.messageType === BROADCAST ? TEXT_MESSAGE_BROADCAST : CAMPAIGN_AUTOMATION;
            }

            return this.sourceTitle;
        },

        isOptedIn() {
            return [SINGLE_OPT_IN, DOUBLE_OPT_IN].includes(this.optStatus);
        },

        isOptedOut() {
            return this.optStatus === OPT_OUT;
        },

        optStatusLabel() {
            switch (this.optStatus) {
            case OPT_OUT:
                return 'Opted out';
            case SINGLE_OPT_IN:
            case DOUBLE_OPT_IN:
                return 'Opted in';
            default:
                return 'Pending opt in';
            }
        },

        optStatusType() {
            switch (this.optStatus) {
            case OPT_OUT:
                return 'error';
            case SINGLE_OPT_IN:
            case DOUBLE_OPT_IN:
                return 'good';
            default:
                return 'default';
            }
        },
    },

    methods: {
        getConversationDate({ updateTime }) {
            const time = moment(updateTime);
            const day = moment(updateTime).startOf('day');
            const now = moment().startOf('day');
            const daysSince = now.diff(day, 'days');
            let lastUpdated;

            if (daysSince === 0) {
                lastUpdated = time.format('h:mm a');
            } else if (daysSince >= 0 && daysSince < 2) {
                lastUpdated = 'yesterday';
            } else if (daysSince >= 2 && daysSince < 7) {
                lastUpdated = time.format('dddd');
            } else if (daysSince >= 7 && daysSince < 365) {
                lastUpdated = time.format('MMM Do');
            } else {
                lastUpdated = time.format('M/D/YY');
            }

            return lastUpdated;
        },

    },
};
</script>

<style lang="scss">
// Without this, the date text is getting wrapped when the message content takes the full width
.message-history-item .trailing-slot {
    flex: 0 0 auto;
}
</style>

<style lang="scss" scoped>
.item-description {
    display: flex;
    flex-direction: column;
    min-width: px-to-rem(700);
}

.description-header,
.description-info {
    padding-bottom: $spacing-075;
}

.phone-type-popover {
    --popover-min-width: auto;
    --popover-width: auto;
}

.contact-name {
    @include padding-end($spacing-100);
    font-size: $font-size-md;
    color: $color-text-normal;
    line-height: $line-height-lg;
}

.phone-type,
.opt-status {
    padding: 0;
    background: transparent;
}

.opt-status {
    @include padding-start($spacing-200);
}

.phone-number,
.opt-status-tooltip {
    color: $color-text-normal;
    padding: $spacing-200;
    font-size: $font-size-md;
}

.via {
    @include padding-end($spacing-100);
}

.via,
.message {
    color: $color-text-subtle;
    overflow-wrap: break-word;
}

.deleted-item-alert {
    margin: $spacing-200 0 0 0;
}
</style>
